import { CSSProperties } from "react";
import {
    PartialBrandTheme,
    PartialQuestThemeData,
    OnboardingThemeConfig,
    OnboardingStyleConfig,
    DEFAULT_BRAND_THEME,
    DEFAULT_QUEST_THEME,
} from "./tutorial.types";
import { hexToGlassColor } from "../../../../general";

class OnboardingThemeManager {
    themeConfig: OnboardingThemeConfig;
    styleConfig: OnboardingStyleConfig;
    questThemeData: PartialQuestThemeData;
    brandTheme: PartialBrandTheme;

    constructor(
        themeConfig: OnboardingThemeConfig = {},
        styleConfig: OnboardingStyleConfig = {},
        questThemeData: PartialQuestThemeData = DEFAULT_QUEST_THEME,
        brandTheme: PartialBrandTheme = DEFAULT_BRAND_THEME
    ) {
        this.themeConfig = themeConfig;
        this.styleConfig = styleConfig;
        this.questThemeData = { ...DEFAULT_QUEST_THEME, ...questThemeData };
        this.brandTheme = { ...DEFAULT_BRAND_THEME, ...brandTheme };
    }

    // done
    getStyleValueAsString(path: string, defaultValue: string = ""): string {
        const keys = path.split(".");
        let value: any = this.styleConfig;

        for (const key of keys) {
            if (value && typeof value === "object") {
                value = value[key];
            } else {
                return defaultValue;
            }
        }
        return typeof value === "string" ? value : defaultValue;
    }

    // done
    getStyleValueAsObject<T>(path: string, defaultValue: T): T {
        const keys = path.split(".");
        let value: any = this.styleConfig;

        for (const key of keys) {
            if (value && typeof value === "object") {
                value = value[key];
            } else {
                return defaultValue;
            }
        }

        return (typeof value === "object" ? value : defaultValue) as T;
    }

    // done
    getContainerStyles(): CSSProperties {
        return {
            background: this.getBackgroundColor(),
            height: this.getStyleValueAsString("Form.height", "auto"),
            borderRadius: this.getBorderRadius(),
            fontFamily: this.getFontFamily(),
            ...this.styleConfig?.Form,
        };
    }

    // done
    getTopBarStyles() {
        return {
            headingStyle: {
                color:
                    this.getStyleValueAsString("Heading.color") ||
                    this.brandTheme?.titleColor ||
                    this.brandTheme?.primaryColor ||
                    this.themeConfig?.primaryColor ||
                    "#000000",
                ...this.styleConfig?.Heading,
            },
            subHeadingStyle: {
                color:
                    this.getStyleValueAsString("Description.color") ||
                    this.brandTheme?.contentColor ||
                    this.themeConfig?.secondaryColor ||
                    "#666666",
                ...this.styleConfig?.Description,
            },
            topbarStyle: {
                ...this.styleConfig?.Topbar,
            },
        };
    }

    // done
    getLabelStyles(): CSSProperties {
        return {
            color:
                this.styleConfig?.Label?.color ||
                this.brandTheme?.contentColor ||
                this.brandTheme?.primaryColor ||
                this.themeConfig?.primaryColor,
            ...this.styleConfig?.Label,
        };
    }

    // done
    getTextInputErrorText(): string {
        return this.styleConfig?.TextError?.text || "Invalid text format";
    }

    // done
    getTextInputErrorStyles(): CSSProperties {
        return {
            ...this.styleConfig?.TextError?.errorStyle
        };
    }

    // done
    getTextInputStyles(): CSSProperties {
        return {
            borderColor:
                this.styleConfig?.Input?.borderColor,
            color:
                this.styleConfig?.Input?.color ||
                this.brandTheme?.titleColor ||
                this.themeConfig?.primaryColor,
            ...this.styleConfig?.Input,
        };
    }

    // done
    getEmailIconColor(): string {
        return this.styleConfig?.Input?.color ||
            this?.brandTheme?.titleColor ||
            this?.themeConfig?.primaryColor ||
            "#B9B9B9"
    }

    // done
    getEmailInputErrorText(): string {
        return this.styleConfig?.EmailError?.text || "Invalid text format";
    }

    // done
    getEMailInputErrorStyles(): CSSProperties {
        return {
            ...this.styleConfig?.EmailError?.errorStyle
        };
    }

    // done
    getNumberInputErrorText(): string {
        return this.styleConfig?.NumberError?.text || "Invalid text format";
    }

    // done
    getNumberInputErrorStyles(): CSSProperties {
        return {
            ...this.styleConfig?.NumberError?.errorStyle
        };
    }

    // done
    getTextAreaInputStyles(): CSSProperties {
        return {
            borderColor:
                this.styleConfig?.TextArea?.borderColor,
            color:
                this.styleConfig?.TextArea?.color ||
                this.brandTheme?.titleColor ||
                this.themeConfig?.primaryColor,
            ...this.styleConfig?.TextArea,
        };
    }

    // done
    getTextAreaInputErrorText(): string {
        return this.styleConfig?.TextAreaError?.text || "Invalid text format";
    }

    // done
    getTextAreaInputErrorStyles(): CSSProperties {
        return {
            ...this.styleConfig?.TextAreaError?.errorStyle
        };
    }

    // for single choice 
    // done
    getSingleChoiceStyles(): CSSProperties {
        return {
            borderColor:
                this.styleConfig?.SingleChoice?.style?.borderColor,
            color:
                this.styleConfig?.SingleChoice?.style?.color ||
                this.brandTheme?.contentColor ||
                this.themeConfig?.primaryColor,
            ...this.styleConfig?.SingleChoice?.style,
        };
    }

    // done
    getSingleChoiceHoverBackground(): string {
        return this.styleConfig?.SingleChoice?.hoverBackground || hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 0.5);
    }
    // done
    getSingleChoiceSelectedStyles(): CSSProperties {
        return {
            accentColor:
                this.styleConfig?.SingleChoice?.selectedStyle?.accentColor ||
                this.themeConfig?.primaryColor ||
                this.questThemeData?.accentColor ||
                this.brandTheme?.accentColor ||
                this.brandTheme?.primaryColor,
            ...this.styleConfig?.SingleChoice?.selectedStyle,
        };
    }

    // for muli chice 
    // done
    getMultiChoiceStyles(): CSSProperties {
        return {
            borderColor:
                this.styleConfig?.MultiChoice?.style?.borderColor,
            color:
                this.styleConfig?.MultiChoice?.style?.color ||
                this.brandTheme?.contentColor ||
                this.themeConfig?.primaryColor,
            ...this.styleConfig?.MultiChoice?.style,
        };
    }

    // done
    getMultiChoiceSelectedStyles(model: string): CSSProperties {
        return {
            color:
                this.styleConfig?.MultiChoice?.selectedStyle?.accentColor ||
                model == 'modal1' && this.questThemeData?.accentColor ||
                model == 'modal1' && this.brandTheme?.accentColor ||
                model == 'modal2' && 'white' ||
                this.brandTheme?.primaryColor ||
                this.themeConfig?.primaryColor,
            backgroundColor: model == 'model1' ? (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) : "",
            background: model == 'modal2' ? (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) : "",
            ...this.styleConfig?.MultiChoice?.selectedStyle,
        };
    }

    // done
    getFontFamily(): string {
        return (
            this.brandTheme?.fontFamily ||
            this.themeConfig?.fontFamily ||
            "'Figtree', sans-serif"
        );
    }

    // npt in use
    getBackgroundColor(): string {
        return (
            this.getStyleValueAsString("Form.backgroundColor", "") ||
            this.brandTheme?.background ||
            this.themeConfig?.backgroundColor || "white"
        );
    }

    // done
    getSecondaryButtonStyles(): CSSProperties {
        return {
            borderColor:
                this.styleConfig?.SecondaryButton?.borderColor,
            background:
                this.styleConfig?.SecondaryButton?.backgroundColor || this.themeConfig?.backgroundColor,
            color:
                this.styleConfig?.SecondaryButton?.color ||
                this.themeConfig?.primaryColor,
            borderRadius: this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px",
            ...this.styleConfig?.SecondaryButton,
        }
    }
    // done
    getPrimaryButtonStyles(): CSSProperties {
        return {
            background:
                this.styleConfig?.PrimaryButton?.background ||
                this.brandTheme?.primaryColor ||
                this.questThemeData?.accentColor,
            borderRadius: this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px",
            ...this.styleConfig?.PrimaryButton,
        }
    }

    // done
    getSecondaryButtonAeroStyles(): CSSProperties {
        return {
            height: this.styleConfig?.SecondaryButton?.width || "44px",
            width: this.styleConfig?.SecondaryButton?.width || "44px",
            borderRadius: this.styleConfig?.SecondaryButton?.borderRadius || this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "50%",
            padding: this.styleConfig?.SecondaryButton?.padding || "10px",
            border: this.styleConfig?.SecondaryButton?.border || "1.5px solid #afafaf",
            borderColor: this.styleConfig?.SecondaryButton?.borderColor,
            backgroundColor: this.styleConfig?.SecondaryButton?.backgroundColor || this.themeConfig?.backgroundColor,
            color: this.styleConfig?.SecondaryButton?.color || this.themeConfig?.primaryColor,
            ...this.styleConfig?.SecondaryButton,
        }
    }

    // done
    getPrimaryButtonAeroStyles(): CSSProperties {
        return {
            height: this.styleConfig?.PrimaryButton?.width || "44px",
            width: this.styleConfig?.PrimaryButton?.width || "44px",
            borderRadius: this.styleConfig?.PrimaryButton?.borderRadius || this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "50%",
            padding: this.styleConfig?.PrimaryButton?.padding || "10px",
            border: this.styleConfig?.PrimaryButton?.border || "1.5px solid #afafaf",
            borderColor: this.styleConfig?.PrimaryButton?.borderColor,
            background: this.styleConfig?.PrimaryButton?.background || this.brandTheme?.primaryColor || this.questThemeData?.accentColor,
            ...this.styleConfig?.PrimaryButton,
        }
    }

    // done
    getNormalButtonStyles(): CSSProperties {
        return {
            border: this.styleConfig?.SecondaryButton?.border || "1.5px solid #afafaf",

            background: this.styleConfig?.PrimaryButton?.background || this.brandTheme?.primaryColor || this.questThemeData?.accentColor,
            ...this.styleConfig?.PrimaryButton,
        }
    }

    // done
    getProgressBarStyles(): string {
        return (this.styleConfig?.ProgressBar?.completeTabColor || this.brandTheme?.titleColor || this?.questThemeData?.accentColor || "#098849")
    }

    getProgressBarBorderStyles(): string {
        return (this.styleConfig?.ProgressBar?.completeTabColor || this.brandTheme?.primaryColor || this?.questThemeData?.accentColor || "#098849")
    }

    getCheckBoxColor(): string {
        return (this.styleConfig?.MultiChoice?.selectedStyle?.color || this.brandTheme?.primaryColor || this?.questThemeData?.accentColor || "#098849")
    }

    // done
    getProgressBarCurrentTabColor(): string {
        return (this.styleConfig?.ProgressBar?.currentTabColor || "#2C2C2C")
    }
    // done
    getProgressBarPendingTabColor(): string {
        return (this.styleConfig?.ProgressBar?.currentTabColor || "#ECECEC")
    }


    getBorderRadius(): string {
        return (
            this.questThemeData?.borderRadius ||
            this.brandTheme?.borderRadius ||
            this.getStyleValueAsString("Form.borderRadius", "8px")
        );
    }

    // Heading and Description styles
    getHeadingStyles(): CSSProperties {
        return {
            color:
                this.getStyleValueAsString("Heading.color", "") ||
                this.brandTheme?.primaryColor ||
                this.themeConfig?.primaryColor ||
                "#000000",
            ...this.styleConfig?.Heading,
        };
    }

    getDescriptionStyles(): CSSProperties {
        return {
            color:
                this.getStyleValueAsString("Description.color", "") ||
                this.brandTheme?.secondaryColor ||
                this.themeConfig?.secondaryColor ||
                "#666666",
            ...this.styleConfig?.Description,
        };
    }

    // Footer styles
    getFooterStyles() {
        const footerConfig = this.styleConfig?.Footer || {};
        return {
            container: {
                background: this.getBackgroundColor(),
                borderRadius: this.styleConfig?.Form?.borderRadius || `0px 0px ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"} ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"}`,
                ...footerConfig.FooterStyle,
            },
            text: footerConfig.FooterTextStyle || {},
            icon: footerConfig.FooterIcon || {},
            content: {
                text: footerConfig.FooterText || "",
                link: footerConfig.FooterLink || "",
            },
        };
    }
    getOnboardingFooterStyles() {
        return {
            FooterStyle: {
                background: this.styleConfig?.Footer?.FooterStyle?.background || this.styleConfig?.Footer?.FooterStyle?.backgroundColor || this.getStyleValueAsString("Form.backgroundColor", "") || this.brandTheme?.background || this.themeConfig?.backgroundColor,
                borderRadius: this.styleConfig?.Form?.borderRadius || `0px 0px ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"} ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"}`,
                ...this.styleConfig?.Footer?.FooterStyle,
            },
            FooterTextStyle: {
                ...this.styleConfig?.Footer?.FooterTextStyle
            },
            FooterIcon: {
                ...this.styleConfig?.Footer?.FooterIcon
            },
            FooterText: this.styleConfig?.Footer?.FooterText,
            FooterLink: this.styleConfig?.Footer?.FooterLink
        };
    }

    // Update methods for dynamic configuration changes
    updateThemeConfig(newConfig: Partial<OnboardingThemeConfig>): void {
        this.themeConfig = { ...this.themeConfig, ...newConfig };
    }

    updateStyleConfig(newConfig: Partial<OnboardingStyleConfig>): void {
        this.styleConfig = {
            ...this.styleConfig,
            ...newConfig,
            Footer: {
                ...this.styleConfig?.Footer,
                ...newConfig?.Footer,
            },
        };
    }

    updateQuestTheme(newTheme: PartialQuestThemeData): void {
        this.questThemeData = { ...this.questThemeData, ...newTheme };
    }

    updateBrandTheme(newTheme: PartialBrandTheme): void {
        this.brandTheme = { ...this.brandTheme, ...newTheme };
    }



    // Merging and computing dynamic styles
    mergeStyles(
        baseStyles: CSSProperties,
        overrideStyles?: CSSProperties
    ): CSSProperties {
        return {
            ...baseStyles,
            ...(overrideStyles || {}),
        };
    }

    getComputedStyle(
        path: string,
        defaultStyles: CSSProperties = {}
    ): CSSProperties {
        return this.mergeStyles(
            defaultStyles,
            this.getStyleValueAsObject(path, {})
        );
    }
}

export default OnboardingThemeManager;
